import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useStripe } from "@stripe/react-stripe-js";
import {
  Alert,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  List,
  ListItem,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import {
  getPaymentAuthenticationRequired,
  isApiError,
  isTerminalPaymentAttemptError,
} from "@bw/shared/api";
import {
  useWaiverGate,
  useFrontendConfig,
  usePaymentMethods,
  useWalletSetupIntent,
  useStripeIntentAuthentication,
  paymentQueryKeys,
} from "@bw/shared/hooks";
import { playerCardIdentityDisplay } from "@bw/shared/components";
import {
  formatMoney,
  reconcileSavedPaymentMethodSelection,
  renderMarkdown,
} from "@bw/shared/utils";
import type {
  TournamentEventResponse,
  DrawResponse,
  EntryCreate,
  SoloEntryCreate,
  TournamentResponse,
  TournamentRegistrationQuote,
  UserSearchResultResponse,
} from "@bw/shared/types/generated";
import { PaymentMethodsLoadError } from "../../../common/components/PaymentMethodsLoadError";
import { MaterialPlayerCard } from "../../../common/components/MaterialPlayerCard";
import {
  MaterialAddCardForm,
  MaterialConnectedElementsProvider,
  MaterialPaymentMethodPicker,
  MaterialWaiverGate,
} from "../../../common/components/payment";
import { useRegisterEntry } from "../hooks/useRegisterEntry";
import { useEnterSolo } from "../hooks/useEnterSolo";
import { usePartnerSearch } from "../hooks/usePartnerSearch";
import { useAbandonRegistrationAttempt } from "../hooks/useAbandonRegistrationAttempt";
import { RegistrationCardSetupChallenge } from "./RegistrationCardSetupChallenge";
import { cardSetupClientSecret as scaClientSecret } from "../utils/cardSetupAuthentication";
import { getTournamentRegistrationQuoteState } from "../utils/entryFee";
import {
  clearTournamentRegistrationAttempt,
  loadTournamentRegistrationAttempt,
  storeTournamentRegistrationAttempt,
  type TournamentRegistrationAttempt,
  type TournamentRegistrationMode,
} from "../utils/registrationAttemptStorage";

/** Debounce window (ms) before a typed partner query fires a search. */
const PARTNER_SEARCH_DEBOUNCE_MS = 250;

/** A pragmatic email-format check for the minor guardian-email field (#1684) —
 *  UX only, to block an obviously-malformed address before the round-trip. The
 *  BACKEND owns the authoritative validation; this never substitutes for it. */
const GUARDIAN_EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
function isValidEmail(value: string): boolean {
  return GUARDIAN_EMAIL_RE.test(value.trim());
}

function useDebouncedValue<T>(value: T, delayMs: number): T {
  const [debounced, setDebounced] = useState(value);
  useEffect(() => {
    const id = setTimeout(() => setDebounced(value), delayMs);
    return () => clearTimeout(id);
  }, [value, delayMs]);
  return debounced;
}

/**
 * TournamentRegisterDialog (EPIC 1.2) — the PER-EVENT register flow (FREE + PAID),
 * opened from a TournamentEventRow's Register action. This is the multi-event
 * successor to the single-select TournamentRegisterPanel: the SAME money / waiver /
 * partner logic, applied to ONE already-chosen event so the player can register
 * for several events independently.
 *
 * MONEY behavior branches only on the signed-in player's backend quote:
 *   - amount 0 / requires_payment false → FREE (no card, submit { draw_id });
 *     positive / true → PAID (PaymentMethodPicker keyed by facility, submit
 *     { draw_id, payment_method_id }); missing/inconsistent → unavailable.
 *   - External charge is server-side inside register; a non-2xx REJECTS so a failed
 *     charge can never flip the row to "registered".
 *   - One-charge guard: Register is disabled while register.isPending OR after
 *     register.isSuccess; the click handler early-returns on either — a double
 *     click / Enter-twice fires register EXACTLY ONCE.
 *   - The selected card is never cleared on error (retry-ready).
 *
 * The dialog owns the event's draw choice (when the event has >1 draw) and the
 * doubles partner picker (reused from the panel) — the row passes the event + its
 * draws. On SUCCESS it invalidates the tournament-scoped registrations + eligibility
 * queries so the list re-reads the server's truth (the row flips to Registered),
 * then calls onRegistered() to close. The accessible-name and chrome mirror the
 * withdraw dialog (WAI-ARIA modal: focus on open, Escape/scrim dismiss except
 * mid-charge).
 */
/** The dialog's submit mode. SOLO (SLICE 1.4-a) enters a doubles/mixed draw
 *  WITHOUT a partner (pair later); REGISTER is the normal per-event flow. */
type RegisterDialogMode = TournamentRegistrationMode;
type TournamentLegalDocument = NonNullable<
  TournamentResponse["legal_documents"]
>[number];
type ConsentAcceptance = NonNullable<SoloEntryCreate["acceptances"]>[number];
type ForeignRepresentationDeclaration = NonNullable<
  SoloEntryCreate["foreign_representation"]
>;
type ForeignRepresentationCountry = NonNullable<
  ForeignRepresentationDeclaration["countries"]
>[number];
type ForeignDeclarationChoice = "" | "none" | "represented";

interface ForeignCountryDraft {
  country: string;
  period: string;
  lastEvent: string;
}

function emptyForeignCountry(): ForeignCountryDraft {
  return { country: "", period: "", lastEvent: "" };
}

function legalDocumentAcceptanceKey(document: TournamentLegalDocument): string {
  return `${document.document_id}:${document.version}`;
}

function buildForeignRepresentation(
  required: boolean,
  choice: ForeignDeclarationChoice,
  drafts: ForeignCountryDraft[],
): ForeignRepresentationDeclaration | null {
  if (!required || choice === "") return null;
  if (choice === "none") return { declared: true, countries: [] };

  const countries: ForeignRepresentationCountry[] = drafts.map((draft) => ({
    country: draft.country,
    ...(draft.period.trim() ? { period: draft.period.trim() } : {}),
    ...(draft.lastEvent.trim() ? { last_event: draft.lastEvent.trim() } : {}),
  }));
  return { declared: true, countries };
}

/** The arguments the inner body hands the mode-specific submit (the shell builds
 *  the mode's body — EntryCreate vs SoloEntryCreate — from these). */
type SubmitArgs = TournamentRegistrationAttempt;

/** The unified mutation surface the inner body reads. Both `useRegisterEntry`
 *  and `useEnterSolo` expose this shape; the shell wires `submit` to its own
 *  mutation so the body stays mode-agnostic (no money/eligibility logic here). */
interface DialogSubmission {
  isPending: boolean;
  isSuccess: boolean;
  error: unknown;
  /** The created entry's server status on success (EntryResponse /
   *  DrawEntryResponse both carry `status`). A MINOR's entry comes back
   *  `pending_guardian_consent` (#1684) so the dialog shows the "we emailed your
   *  guardian" confirmation instead of closing. null until success. */
  createdStatus: string | null;
  submit: (args: SubmitArgs) => Promise<void>;
}

type RegistrationScaChallenge = {
  kind: "payment" | "setup";
  clientSecret: string;
};

interface DialogProps {
  tournamentId: string;
  facilityId: string;
  event: TournamentEventResponse;
  /** The draws belonging to THIS event (the parent filters by event_id). */
  draws: DrawResponse[];
  /** User-specific amount from the authenticated eligibility response. */
  registrationQuote?: TournamentRegistrationQuote | null;
  /** The signed-in user's uid, for partner self-exclusion. */
  myUid: string | null;
  /** Whether the signed-in registrant is a MINOR (#1684), from the backend's own
   *  `is_minor` profile flag (never client-side age math). When true the dialog
   *  shows a REQUIRED "Parental consent" section (parent/guardian name + email)
   *  and forwards them as guardian_name/guardian_email; the backend holds the
   *  entry `pending_guardian_consent` and emails the guardian. Default false. */
  isMinor?: boolean;
  /** Versioned tournament documents returned by TournamentResponse. */
  legalDocuments?: TournamentLegalDocument[];
  /** Legacy tournament-level terms that require an explicit acceptance. */
  legalNotes?: string | null;
  /** Effective backend compliance policy for foreign representation. */
  requiresForeignDeclaration?: boolean;
  /** Switch a blocked direct doubles attempt to the participant-scoped solo flow. */
  onEnterSolo?: () => void;
  /** Called after a successful register/solo entry (the parent closes + refetches). */
  onRegistered: () => void;
  onClose: () => void;
  /** "register" (default) or "solo" — the latter opened from a doubles/mixed
   *  row's "Enter solo" action. */
  mode?: RegisterDialogMode;
}

/**
 * TournamentRegisterDialog — the public entry point. It only PICKS the mutation
 * shell by `mode` (no hooks here, so the picker is rules-of-hooks clean and the
 * UNUSED mutation hook is never invoked — SOLO mode never calls useRegisterEntry,
 * and vice-versa). Each shell calls EXACTLY ONE mutation hook and renders the
 * shared `RegisterDialogInner` body.
 */
export function TournamentRegisterDialog(props: DialogProps) {
  return props.mode === "solo" ? (
    <SoloDialogShell {...props} />
  ) : (
    <RegisterDialogShell {...props} />
  );
}

/** REGISTER shell — owns the register mutation, builds the EntryCreate body. */
function RegisterDialogShell(props: DialogProps) {
  const register = useRegisterEntry(props.tournamentId);
  const submission: DialogSubmission = {
    isPending: register.isPending,
    isSuccess: register.isSuccess,
    error: register.error,
    createdStatus: register.data?.status ?? null,
    submit: async ({
      eventId,
      drawId,
      isPaid,
      paymentMethodId,
      expectedQuote,
      partnerId,
      guardianName,
      guardianEmail,
      acceptLegalTerms,
      acceptances,
      foreignRepresentation,
    }) => {
      const body: EntryCreate = {
        draw_id: drawId,
        expected_quote: expectedQuote,
        accept_legal_terms: acceptLegalTerms,
        ...(acceptances.length > 0 ? { acceptances } : {}),
        ...(foreignRepresentation
          ? { foreign_representation: foreignRepresentation }
          : {}),
        ...(partnerId ? { partner_id: partnerId } : {}),
        ...(isPaid ? { payment_method_id: paymentMethodId } : {}),
        // #1684: a MINOR's guardian contact rides the entry body verbatim; the
        // backend holds the entry pending_guardian_consent and emails them.
        ...(guardianName ? { guardian_name: guardianName } : {}),
        ...(guardianEmail ? { guardian_email: guardianEmail } : {}),
      };
      await register.mutateAsync({ eventId, body });
    },
  };
  return (
    <RegisterDialogInner {...props} mode="register" submission={submission} />
  );
}

/** SOLO shell (SLICE 1.4-a) — owns the enter-solo mutation, builds the
 *  SoloEntryCreate body. NEVER a fabricated amount/fee; pairing happens later. */
function SoloDialogShell(props: DialogProps) {
  const enterSolo = useEnterSolo(props.tournamentId);
  const submission: DialogSubmission = {
    isPending: enterSolo.isPending,
    isSuccess: enterSolo.isSuccess,
    error: enterSolo.error,
    createdStatus: enterSolo.data?.status ?? null,
    submit: async ({
      drawId,
      isPaid,
      paymentMethodId,
      expectedQuote,
      guardianName,
      guardianEmail,
      acceptLegalTerms,
      acceptances,
      foreignRepresentation,
    }) => {
      const body: SoloEntryCreate = {
        accept_legal_terms: acceptLegalTerms,
        ...(acceptances.length > 0 ? { acceptances } : {}),
        ...(foreignRepresentation
          ? { foreign_representation: foreignRepresentation }
          : {}),
        expected_quote: expectedQuote,
        ...(isPaid ? { payment_method_id: paymentMethodId } : {}),
        // #1684: a MINOR soloist's guardian contact rides the body verbatim.
        ...(guardianName ? { guardian_name: guardianName } : {}),
        ...(guardianEmail ? { guardian_email: guardianEmail } : {}),
      };
      await enterSolo.mutateAsync({ drawId, body });
    },
  };
  return <RegisterDialogInner {...props} mode="solo" submission={submission} />;
}

function RegisterDialogInner({
  tournamentId,
  facilityId,
  event,
  draws,
  registrationQuote,
  myUid,
  isMinor = false,
  legalDocuments = [],
  legalNotes = null,
  requiresForeignDeclaration = false,
  onEnterSolo,
  onRegistered,
  onClose,
  mode,
  submission,
}: DialogProps & { mode: RegisterDialogMode; submission: DialogSubmission }) {
  const isSolo = mode === "solo";
  const paymentSelectionContext = `${facilityId}:${myUid ?? "signed-out"}:${event.event_id}:${mode}`;
  const [persistedAttempt, setPersistedAttempt] =
    useState<TournamentRegistrationAttempt | null>(() =>
      myUid
        ? loadTournamentRegistrationAttempt(
            facilityId,
            myUid,
            event.event_id,
            mode,
          )
        : null,
    );
  const [selectedDrawId, setSelectedDrawId] = useState<string | null>(
    persistedAttempt?.drawId ?? null,
  );
  const [paymentSelection, setPaymentSelection] = useState(() => ({
    context: paymentSelectionContext,
    paymentMethodId: null as string | null,
  }));
  const selectedPmId =
    paymentSelection.context === paymentSelectionContext
      ? paymentSelection.paymentMethodId
      : null;
  const selectPaymentMethod = useCallback(
    (paymentMethodId: string | null) => {
      setPaymentSelection({
        context: paymentSelectionContext,
        paymentMethodId,
      });
    },
    [paymentSelectionContext],
  );
  const initializedPaymentContextRef = useRef<string | null>(
    persistedAttempt ? paymentSelectionContext : null,
  );
  const [selectedPartner, setSelectedPartner] =
    useState<UserSearchResultResponse | null>(null);
  const [partnerQuery, setPartnerQuery] = useState("");
  const [acceptedDocumentKeys, setAcceptedDocumentKeys] = useState<Set<string>>(
    () =>
      new Set(
        persistedAttempt?.acceptances.map(
          (acceptance) => `${acceptance.document_id}:${acceptance.version}`,
        ) ?? [],
      ),
  );
  const [tournamentTermsAccepted, setTournamentTermsAccepted] = useState(
    persistedAttempt?.acceptLegalTerms ?? false,
  );
  const [eventTermsAccepted, setEventTermsAccepted] = useState(
    persistedAttempt?.acceptLegalTerms ?? false,
  );
  const [foreignChoice, setForeignChoice] = useState<ForeignDeclarationChoice>(
    persistedAttempt?.foreignRepresentation
      ? (persistedAttempt.foreignRepresentation.countries?.length ?? 0) > 0
        ? "represented"
        : "none"
      : "",
  );
  const [foreignCountries, setForeignCountries] = useState<
    ForeignCountryDraft[]
  >(() => {
    const countries = persistedAttempt?.foreignRepresentation?.countries ?? [];
    return countries.length > 0
      ? countries.map((country) => ({
          country: country.country,
          period: country.period ?? "",
          lastEvent: country.last_event ?? "",
        }))
      : [emptyForeignCountry()];
  });

  // #1684 — a MINOR registrant's parent/guardian contact. These render + gate the
  // submit ONLY when `isMinor`; an adult never sees them and they stay "" (so the
  // submit body omits guardian fields entirely). Email is format-checked
  // client-side for UX only — the backend owns the authoritative validation.
  const [guardianName, setGuardianName] = useState(
    persistedAttempt?.guardianName ?? "",
  );
  const [guardianEmail, setGuardianEmail] = useState(
    persistedAttempt?.guardianEmail ?? "",
  );
  const guardianEmailValid = isValidEmail(guardianEmail);
  // The minor consent section is complete when the name is non-blank AND the email
  // is a valid format. For an adult the gate is vacuously satisfied.
  const guardianComplete =
    !isMinor || (guardianName.trim().length > 0 && guardianEmailValid);

  // The active draw: the player's pick, else the sole draw when the event has one.
  const activeDraw: DrawResponse | null = useMemo(() => {
    if (selectedDrawId) {
      return draws.find((d) => d.draw_id === selectedDrawId) ?? null;
    }
    return draws.length === 1 ? draws[0] : null;
  }, [draws, selectedDrawId]);

  const drawRequiresPartner = activeDraw?.requires_partner === true;
  const requiredLegalDocuments = useMemo(
    () => legalDocuments.filter((document) => document.requires_signature),
    [legalDocuments],
  );
  const tournamentLegalNotes = legalNotes?.trim() ? legalNotes : null;
  const eventLegalNotes = event.legal_notes?.trim() ? event.legal_notes : null;
  const hasTournamentTerms = tournamentLegalNotes != null;
  const hasEventTerms = eventLegalNotes != null;
  const hasLegacyTerms = hasTournamentTerms || hasEventTerms;
  // Player registration uses the participant-scoped solo-and-pair workflow for
  // every doubles draw. A direct pair cannot safely know or attest to the
  // partner's age, guardian, legal-document, or federation requirements.
  const directPairingBlocked = drawRequiresPartner && !isSolo;
  // SOLO mode DEFERS pairing — the partner picker is suppressed and the
  // partner-chosen gate does not apply (you pair via the finder later). The
  // server still creates a `partner_wanted` entry; the FE never sends a partner.
  const partnerPickerActive =
    drawRequiresPartner && !isSolo && !directPairingBlocked;

  const debouncedPartnerQuery = useDebouncedValue(
    partnerQuery,
    PARTNER_SEARCH_DEBOUNCE_MS,
  );
  const partnerQ = usePartnerSearch(
    partnerPickerActive ? debouncedPartnerQuery : "",
  );
  const partnerResults: UserSearchResultResponse[] = useMemo(
    () => (partnerQ.data ?? []).filter((r) => r.user_id !== myUid),
    [partnerQ.data, myUid],
  );

  const effectiveRegistrationQuote =
    persistedAttempt?.expectedQuote ?? registrationQuote;
  const feeState = getTournamentRegistrationQuoteState(
    effectiveRegistrationQuote,
  );
  const isFree = feeState.kind === "free";
  const isPaid = feeState.kind === "paid";
  const feeUnavailable = feeState.kind === "unavailable";

  const paymentEnabled = isPaid;
  const configQ = useFrontendConfig(facilityId, paymentEnabled);
  const methodsQ = usePaymentMethods(facilityId, paymentEnabled);
  const setupIntent = useWalletSetupIntent();
  const abandonAttempt = useAbandonRegistrationAttempt(tournamentId);
  const [abandonError, setAbandonError] = useState<string | null>(null);

  const waiver = useWaiverGate(facilityId);
  // React Query may publish isAccepting on the next task. Lock dismissal at the
  // click itself so a rapid Accept → Cancel cannot close before the waiver POST
  // settles and leave the eligibility row with a stale waiver-only verdict.
  const waiverAcceptStartedRef = useRef(false);

  const createSetupIntentSecret = useCallback(
    async (idempotencyKey: string) => {
      const res = await setupIntent.mutateAsync(idempotencyKey);
      return res.client_secret;
    },
    [setupIntent],
  );

  const doneButtonRef = useRef<HTMLButtonElement>(null);
  const [scaProcessing, setScaProcessing] = useState(false);
  const [cardSetupBusy, setCardSetupBusy] = useState(false);
  // #1692 — the exact args of the LAST submit, captured so the SCA-success path
  // can RETRY the registration with the identical body (the card the SetupIntent
  // client_secret was minted for) — never a rebuilt-from-current-state body that
  // could drift if the player edited the form mid-challenge.
  const lastSubmitArgsRef = useRef<SubmitArgs | null>(persistedAttempt);

  // #1692 — a paid MINOR's card is SAVED off-session at registration; if that save
  // needs 3-D Secure / SCA the backend fails closed (no entry) and returns 402
  // PAYMENT_AUTHENTICATION_REQUIRED + a client_secret. We surface a dedicated SCA
  // panel (driven through Stripe.js) instead of the generic decline copy, and
  // retry the registration once the challenge clears. null for any other outcome.
  const detectedPaymentAuthentication = getPaymentAuthenticationRequired(
    submission.error,
  );
  const detectedPaymentScaSecret =
    detectedPaymentAuthentication?.clientSecret ?? null;
  const detectedSetupScaSecret = detectedPaymentScaSecret
    ? null
    : scaClientSecret(submission.error);
  const detectedScaKind: RegistrationScaChallenge["kind"] | null =
    detectedPaymentScaSecret
      ? "payment"
      : detectedSetupScaSecret
        ? "setup"
        : null;
  const detectedScaSecret = detectedPaymentScaSecret ?? detectedSetupScaSecret;
  const [activeScaChallenge, setActiveScaChallenge] =
    useState<RegistrationScaChallenge | null>(null);

  // React Query clears mutation.error when a retry starts. Preserve the active
  // challenge across that transition so Stripe confirmation state survives a
  // failed application reconciliation and can be retried without reconfirming.
  useEffect(() => {
    if (detectedScaKind && detectedScaSecret) {
      const attempt = lastSubmitArgsRef.current;
      if (attempt?.isPaid) setPersistedAttempt(attempt);
      setActiveScaChallenge((current) =>
        current?.kind === detectedScaKind &&
        current.clientSecret === detectedScaSecret
          ? current
          : { kind: detectedScaKind, clientSecret: detectedScaSecret },
      );
      return;
    }
    if (isTerminalPaymentAttemptError(submission.error)) {
      setActiveScaChallenge(null);
      const attempt = lastSubmitArgsRef.current;
      if (attempt && myUid) {
        clearTournamentRegistrationAttempt(
          facilityId,
          myUid,
          event.event_id,
          mode,
          attempt,
        );
        lastSubmitArgsRef.current = null;
        setPersistedAttempt(null);
      }
      return;
    }
    if (submission.error != null) {
      const attempt = lastSubmitArgsRef.current;
      if (attempt?.isPaid) setPersistedAttempt(attempt);
    }
  }, [
    detectedScaKind,
    detectedScaSecret,
    event.event_id,
    facilityId,
    mode,
    myUid,
    submission.error,
  ]);

  const scaChallenge: RegistrationScaChallenge | null =
    detectedScaKind && detectedScaSecret
      ? { kind: detectedScaKind, clientSecret: detectedScaSecret }
      : activeScaChallenge;
  const scaRetryActive = scaChallenge != null;
  const recoveryPending = persistedAttempt !== null;
  const checkoutOperationLocked =
    submission.isPending ||
    submission.isSuccess ||
    abandonAttempt.isPending ||
    scaRetryActive ||
    recoveryPending;
  const formControlsLocked = checkoutOperationLocked || cardSetupBusy;
  const paymentMethodsSettled =
    isPaid &&
    !recoveryPending &&
    methodsQ.data !== undefined &&
    !methodsQ.isFetching &&
    !methodsQ.isError;
  useEffect(() => {
    if (recoveryPending) {
      initializedPaymentContextRef.current = paymentSelectionContext;
      return;
    }
    if (!paymentMethodsSettled || methodsQ.data === undefined) return;
    const initializeProviderDefault =
      initializedPaymentContextRef.current !== paymentSelectionContext;
    initializedPaymentContextRef.current = paymentSelectionContext;
    setPaymentSelection((current) => {
      const currentId =
        current.context === paymentSelectionContext
          ? current.paymentMethodId
          : null;
      const paymentMethodId = reconcileSavedPaymentMethodSelection(
        methodsQ.data,
        currentId,
        initializeProviderDefault,
      );
      if (
        current.context === paymentSelectionContext &&
        current.paymentMethodId === paymentMethodId
      ) {
        return current;
      }
      return { context: paymentSelectionContext, paymentMethodId };
    });
  }, [
    methodsQ.data,
    paymentMethodsSettled,
    paymentSelectionContext,
    recoveryPending,
  ]);
  const chargeReadyPmId =
    paymentMethodsSettled && methodsQ.data !== undefined
      ? reconcileSavedPaymentMethodSelection(methodsQ.data, selectedPmId, false)
      : null;

  // A MINOR's entry is held by the backend in `pending_guardian_consent` (#1684).
  // On that outcome the dialog must NOT auto-close — it shows the "we emailed your
  // guardian" confirmation so the player knows their place is held pending consent.
  const isPendingConsent =
    submission.isSuccess &&
    submission.createdStatus === "pending_guardian_consent";

  // On success, invalidate the tournament-scoped lists so the row reflects the
  // server's refetched truth, then let the parent close — UNLESS the entry is held
  // pending guardian consent, in which case we keep the dialog open to show the
  // confirmation (the player dismisses it themselves). A FAILED submit never
  // reaches here (onSuccess only) → the row stays not-entered.
  useEffect(() => {
    if (submission.isSuccess) {
      const attempt = lastSubmitArgsRef.current;
      if (attempt && myUid) {
        clearTournamentRegistrationAttempt(
          facilityId,
          myUid,
          event.event_id,
          mode,
          attempt,
        );
        lastSubmitArgsRef.current = null;
        setPersistedAttempt(null);
      }
      if (!isPendingConsent) onRegistered();
    }
  }, [
    event.event_id,
    facilityId,
    isPendingConsent,
    mode,
    myUid,
    onRegistered,
    submission.isSuccess,
  ]);

  // When the form is replaced by the pending-consent confirmation (#1684), move
  // focus to its Done control so keyboard focus is never stranded on a now-unmounted
  // form node.
  useEffect(() => {
    if (isPendingConsent) {
      doneButtonRef.current?.focus();
    }
  }, [isPendingConsent]);

  // Escape, Cancel, and scrim dismissal share one lock while waiver acceptance,
  // money, or SCA reconciliation is in flight. Closing before waiver persistence
  // could refetch eligibility too early and strand the row on a stale verdict.
  const locked =
    waiver.isAccepting ||
    submission.isPending ||
    submission.isSuccess ||
    abandonAttempt.isPending ||
    scaProcessing ||
    cardSetupBusy;
  const closeIfUnlocked = () => {
    if (!locked && !waiverAcceptStartedRef.current) onClose();
  };
  const isFull =
    event.max_entries != null &&
    event.entry_count != null &&
    event.entry_count >= event.max_entries;

  const legalDocumentsComplete = requiredLegalDocuments.every((document) =>
    acceptedDocumentKeys.has(legalDocumentAcceptanceKey(document)),
  );
  const legacyTermsComplete =
    (!hasTournamentTerms || tournamentTermsAccepted) &&
    (!hasEventTerms || eventTermsAccepted);
  const foreignCountriesComplete =
    foreignChoice === "represented" &&
    foreignCountries.length > 0 &&
    foreignCountries.every((country) => /^[A-Z]{3}$/.test(country.country));
  const foreignDeclarationComplete =
    !requiresForeignDeclaration ||
    foreignChoice === "none" ||
    foreignCountriesComplete;
  const complianceComplete =
    legalDocumentsComplete && legacyTermsComplete && foreignDeclarationComplete;

  const canSubmit = recoveryPending
    ? !submission.isPending && !submission.isSuccess && scaChallenge == null
    : activeDraw != null &&
      !isFull &&
      !directPairingBlocked &&
      !feeUnavailable &&
      (!isPaid || chargeReadyPmId != null) &&
      (!partnerPickerActive || selectedPartner != null) &&
      // #1684: a minor cannot submit until the guardian name + a valid email are in.
      guardianComplete &&
      complianceComplete &&
      !waiver.isBlocked &&
      !submission.isPending &&
      !submission.isSuccess &&
      !cardSetupBusy &&
      // #1692: while a card-save SCA challenge is pending, the player completes it
      // via the SCA panel (which retries) — re-clicking Register would re-fire an
      // entry that the backend has already refused pending authentication.
      scaChallenge == null;

  function handleSubmit() {
    if (cardSetupBusy) return;
    if (recoveryPending) {
      const attempt = lastSubmitArgsRef.current;
      if (
        !attempt ||
        submission.isPending ||
        submission.isSuccess ||
        scaRetryActive
      )
        return;
      void submission.submit(attempt).catch(() => undefined);
      return;
    }
    if (
      !activeDraw ||
      submission.isPending ||
      submission.isSuccess ||
      scaRetryActive
    )
      return;
    if (isFull || feeUnavailable) return;
    if (directPairingBlocked || !complianceComplete) return;
    if (isPaid && !chargeReadyPmId) return;
    if (partnerPickerActive && !selectedPartner) return;
    if (partnerPickerActive && selectedPartner?.user_id === myUid) return;
    // A minor must supply a valid guardian name + email before any entry/charge.
    if (!guardianComplete) return;
    if (waiver.isBlocked) return;
    if (registrationQuote == null) return;

    const acceptances: ConsentAcceptance[] = requiredLegalDocuments.map(
      (document) => ({
        document_id: document.document_id,
        version: document.version,
        guardian_signature: false,
      }),
    );
    const foreignRepresentation = buildForeignRepresentation(
      requiresForeignDeclaration,
      foreignChoice,
      foreignCountries,
    );

    // The shell builds the mode's body (EntryCreate vs SoloEntryCreate) — the
    // inner body forwards the server quote unchanged, and SOLO never sends a partner.
    // Guardian contact is sent ONLY for a minor (trimmed); an adult sends null so
    // the body omits the fields entirely.
    const args: SubmitArgs = {
      eventId: event.event_id,
      drawId: activeDraw.draw_id,
      isPaid,
      paymentMethodId: chargeReadyPmId,
      expectedQuote: registrationQuote,
      partnerId:
        partnerPickerActive && selectedPartner ? selectedPartner.user_id : null,
      guardianName: isMinor ? guardianName.trim() : null,
      guardianEmail: isMinor ? guardianEmail.trim() : null,
      acceptLegalTerms: hasLegacyTerms && legacyTermsComplete,
      acceptances,
      foreignRepresentation,
    };
    // Capture for a possible SCA retry (#1692), then submit.
    lastSubmitArgsRef.current = args;
    if (myUid && args.isPaid) {
      storeTournamentRegistrationAttempt(
        facilityId,
        myUid,
        event.event_id,
        mode,
        args,
      );
    }
    // React Query owns the visible initial-submit error state. Consume the
    // mutateAsync rejection here; SCA retries await and surface it below.
    void submission.submit(args).catch(() => undefined);
  }

  // #1692 — after the player clears the card-save SCA challenge, retry the
  // registration with the IDENTICAL captured body (the card is now saved, so the
  // backend holds the entry pending_guardian_consent). The backend stays the gate.
  async function handleScaAuthenticated(
    authenticatedClientSecret: string,
  ): Promise<void> {
    if (scaChallenge?.clientSecret !== authenticatedClientSecret) return;
    const args = lastSubmitArgsRef.current;
    if (args) await submission.submit(args);
  }

  async function handleUseAnotherCard(): Promise<void> {
    const attempt = persistedAttempt;
    if (!attempt || !myUid || abandonAttempt.isPending) return;

    setAbandonError(null);
    try {
      const resolution = await abandonAttempt.mutateAsync(attempt.drawId);
      if (
        resolution.outcome !== "released" &&
        resolution.outcome !== "registered"
      ) {
        throw new Error("Unknown registration payment resolution");
      }

      clearTournamentRegistrationAttempt(
        facilityId,
        myUid,
        event.event_id,
        mode,
        attempt,
      );
      lastSubmitArgsRef.current = null;
      setPersistedAttempt(null);
      setActiveScaChallenge(null);
      selectPaymentMethod(null);

      if (resolution.outcome === "registered") onRegistered();
    } catch {
      setAbandonError(
        "We could not safely release the saved payment attempt. Try again before choosing another card.",
      );
    }
  }

  // Suppress the generic decline copy while an SCA challenge is in play — the
  // dedicated SCA panel owns that surface (#1692).
  const submitError = scaChallenge ? null : mapEntryError(submission.error);

  return (
    <Dialog
      open
      fullWidth
      maxWidth="sm"
      onClose={closeIfUnlocked}
      aria-label={
        isSolo ? `Enter ${event.name} solo` : `Register for ${event.name}`
      }
    >
      <DialogTitle>
        {isSolo ? `Enter ${event.name} solo` : `Register for ${event.name}`}
      </DialogTitle>

      <DialogContent>
        {isPendingConsent ? (
          // #1684 — the entry is held pending guardian consent. Replace the whole
          // form with an honest confirmation: the place is held, the guardian was
          // emailed, the player needs to do nothing else now.
          <GuardianConsentPending
            onDone={onClose}
            doneRef={doneButtonRef}
            isPaid={isPaid}
          />
        ) : (
          <Stack spacing={2.5}>
            {/* Draw selection — only when this event has more than one draw. Chosen by
            NAME (never a uuid); the chosen draw_id rides the submit body. */}
            {draws.length > 1 ? (
              <FormControl disabled={formControlsLocked}>
                <FormLabel>Choose a draw</FormLabel>
                <RadioGroup
                  name="register-dialog-draw"
                  value={activeDraw?.draw_id ?? ""}
                  onChange={(event) => {
                    setSelectedDrawId(event.target.value);
                    setSelectedPartner(null);
                    setPartnerQuery("");
                  }}
                >
                  {draws.map((draw) => (
                    <FormControlLabel
                      key={draw.draw_id}
                      value={draw.draw_id}
                      control={<Radio />}
                      label={draw.name}
                    />
                  ))}
                </RadioGroup>
              </FormControl>
            ) : null}

            {/* Fee — the signed-in player's SERVER quote, display-only. */}
            <Typography sx={{ fontWeight: 700 }}>
              {isFree ? (
                "Free entry"
              ) : isPaid ? (
                <>
                  {recoveryPending
                    ? "Finishing saved registration payment: "
                    : "Entry fee: "}
                  <Box component="span" sx={{ color: "primary.main" }}>
                    {formatMoney(
                      effectiveRegistrationQuote?.amount,
                      effectiveRegistrationQuote?.currency,
                    )}
                  </Box>
                </>
              ) : (
                "Entry fee unavailable. Refresh and try again."
              )}
            </Typography>

            {directPairingBlocked ? (
              <Alert
                severity="info"
                role="status"
                action={
                  onEnterSolo ? (
                    <Button color="inherit" size="small" onClick={onEnterSolo}>
                      Enter solo instead
                    </Button>
                  ) : undefined
                }
              >
                Each doubles player must enter separately. Enter solo, then pair
                from My entries.
              </Alert>
            ) : !isFull ? (
              <RegistrationComplianceFields
                documents={requiredLegalDocuments}
                acceptedDocumentKeys={acceptedDocumentKeys}
                onDocumentAcceptanceChange={(acceptanceKey, accepted) => {
                  setAcceptedDocumentKeys((current) => {
                    const next = new Set(current);
                    if (accepted) next.add(acceptanceKey);
                    else next.delete(acceptanceKey);
                    return next;
                  });
                }}
                tournamentLegalNotes={tournamentLegalNotes}
                eventLegalNotes={eventLegalNotes}
                tournamentTermsAccepted={tournamentTermsAccepted}
                eventTermsAccepted={eventTermsAccepted}
                onTournamentTermsAccepted={setTournamentTermsAccepted}
                onEventTermsAccepted={setEventTermsAccepted}
                requiresForeignDeclaration={requiresForeignDeclaration}
                foreignChoice={foreignChoice}
                onForeignChoiceChange={setForeignChoice}
                foreignCountries={foreignCountries}
                onForeignCountryChange={(index, country) =>
                  setForeignCountries((current) =>
                    current.map((item, itemIndex) =>
                      itemIndex === index ? country : item,
                    ),
                  )
                }
                onAddForeignCountry={() =>
                  setForeignCountries((current) => [
                    ...current,
                    emptyForeignCountry(),
                  ])
                }
                onRemoveForeignCountry={(index) =>
                  setForeignCountries((current) =>
                    current.filter((_, itemIndex) => itemIndex !== index),
                  )
                }
                disabled={formControlsLocked}
              />
            ) : null}

            {/* PARENTAL CONSENT (#1684) — a MINOR registrant must supply a parent/
            guardian name + email. The backend holds the entry pending_guardian_
            consent and emails them a confirm link; without these it 400s. Rendered
            ONLY when the signed-in player is a minor (backend `is_minor` flag);
            an adult never sees it. Validation/consent is BACKEND-owned — the email
            format check here is UX-only. */}
            {isMinor && !directPairingBlocked ? (
              <Box
                component="fieldset"
                sx={{
                  m: 0,
                  p: 2,
                  border: 1,
                  borderColor: "divider",
                  borderRadius: 2,
                }}
              >
                <Typography
                  component="legend"
                  variant="subtitle1"
                  sx={{ px: 0.75 }}
                >
                  Parental consent
                </Typography>
                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{ mb: 2 }}
                >
                  Because you&apos;re under 18, a parent or guardian must
                  approve this entry. We&apos;ll email them a link to confirm —
                  your place is held until they do.
                </Typography>
                <Stack spacing={2}>
                  <TextField
                    id="tr-guardian-name"
                    label="Parent/Guardian name"
                    type="text"
                    autoComplete="name"
                    value={guardianName}
                    onChange={(e) => setGuardianName(e.target.value)}
                    disabled={formControlsLocked}
                    required
                    fullWidth
                    slotProps={{
                      htmlInput: { "aria-required": "true" },
                    }}
                  />
                  <TextField
                    id="tr-guardian-email"
                    label="Parent/Guardian email"
                    type="email"
                    autoComplete="email"
                    value={guardianEmail}
                    onChange={(e) => setGuardianEmail(e.target.value)}
                    disabled={formControlsLocked}
                    required
                    fullWidth
                    error={Boolean(guardianEmail) && !guardianEmailValid}
                    helperText={
                      guardianEmail && !guardianEmailValid
                        ? "Enter a valid email address."
                        : " "
                    }
                    slotProps={{
                      htmlInput: {
                        inputMode: "email",
                        "aria-required": "true",
                        ...(guardianEmail && !guardianEmailValid
                          ? {
                              "aria-describedby": "tr-guardian-email-error",
                            }
                          : {}),
                      },
                      formHelperText: {
                        id: "tr-guardian-email-error",
                        role:
                          guardianEmail && !guardianEmailValid
                            ? "alert"
                            : undefined,
                      },
                    }}
                  />
                </Stack>
              </Box>
            ) : null}

            {/* PARTNER picker (REGISTER doubles only, not when full). SUPPRESSED in
            SOLO mode — pairing is deferred to the finder, so the dialog never
            asks for a partner now. */}
            {partnerPickerActive && !isFull ? (
              <PartnerPicker
                query={partnerQuery}
                onQueryChange={setPartnerQuery}
                results={partnerResults}
                isSearching={partnerQ.isFetching}
                hasQuery={debouncedPartnerQuery.trim().length >= 2}
                selectedPartner={selectedPartner}
                onSelect={setSelectedPartner}
                disabled={formControlsLocked}
                onClear={() => {
                  setSelectedPartner(null);
                  setPartnerQuery("");
                }}
              />
            ) : null}

            {isFull ? (
              <Alert severity="warning" role="status">
                This event is full.
              </Alert>
            ) : null}

            {/* PAID payment surface (not when full). */}
            {isPaid && !isFull && !directPairingBlocked ? (
              <Stack spacing={2}>
                {recoveryPending ? (
                  <Button
                    type="button"
                    variant="outlined"
                    size="small"
                    onClick={() => void handleUseAnotherCard()}
                    disabled={abandonAttempt.isPending}
                    aria-busy={abandonAttempt.isPending || undefined}
                  >
                    {abandonAttempt.isPending
                      ? "Releasing payment attempt..."
                      : "Use another card"}
                  </Button>
                ) : null}
                {configQ.isError ? (
                  <Alert severity="error" role="alert">
                    This facility can&apos;t take card payments yet. Please try
                    again later.
                  </Alert>
                ) : methodsQ.isError && scaChallenge === null ? (
                  <PaymentMethodsLoadError
                    retrying={methodsQ.isFetching}
                    onRetry={() => void methodsQ.refetch()}
                  />
                ) : configQ.isLoading || methodsQ.isLoading ? (
                  <Typography color="text.secondary" role="status">
                    Loading payment options…
                  </Typography>
                ) : (
                  <>
                    <MaterialPaymentMethodPicker
                      methods={methodsQ.data ?? []}
                      selectedId={
                        persistedAttempt?.paymentMethodId ?? selectedPmId
                      }
                      onSelect={selectPaymentMethod}
                      disabled={formControlsLocked}
                      allowRemove
                      facilityId={facilityId}
                      paymentMethodsQueryKey={paymentQueryKeys.paymentMethods(
                        facilityId,
                      )}
                    />
                    {configQ.data ? (
                      <MaterialConnectedElementsProvider config={configQ.data}>
                        {!checkoutOperationLocked ? (
                          <MaterialAddCardForm
                            publishableKey={configQ.data.publishable_key}
                            createSetupIntent={createSetupIntentSecret}
                            onBusyChange={setCardSetupBusy}
                            onCardAdded={async (pmId) => {
                              const refreshed = await methodsQ.refetch();
                              const savedMethod = refreshed.data?.find(
                                (method) => method.payment_method_id === pmId,
                              );
                              if (
                                refreshed.isError ||
                                savedMethod?.is_usable !== true
                              ) {
                                throw new Error(
                                  "The saved card is not available yet. Retry to refresh it.",
                                );
                              }
                              selectPaymentMethod(pmId);
                            }}
                          />
                        ) : null}
                        {scaChallenge?.kind === "payment" ? (
                          <RegisterPaymentScaChallenge
                            clientSecret={scaChallenge.clientSecret}
                            onAuthenticated={handleScaAuthenticated}
                            onProcessingChange={setScaProcessing}
                          />
                        ) : scaChallenge?.kind === "setup" ? (
                          <RegistrationCardSetupChallenge
                            clientSecret={scaChallenge.clientSecret}
                            onAuthenticated={handleScaAuthenticated}
                            onProcessingChange={setScaProcessing}
                          />
                        ) : null}
                      </MaterialConnectedElementsProvider>
                    ) : null}
                  </>
                )}
              </Stack>
            ) : null}

            {/* WAIVER gate — the SHARED component (the same gate the booking confirm
            + drop-in check-in use). FAILS CLOSED: while the status read is loading
            or errored, waiver.isBlocked is true and Register stays disabled (the
            submit gate reads isBlocked). On a status-read ERROR we surface an
            honest "couldn't confirm your waiver" + Retry (never a silent dead
            button); otherwise, once status resolves to "required, not accepted",
            the per-person WaiverGate (Read the waiver + accept-for-myself) renders.
            The caller owns useWaiverGate; WaiverGate is pure presentation. */}
            {!isFull && !directPairingBlocked && waiver.statusError ? (
              <Alert
                severity="error"
                role="alert"
                action={
                  <Button
                    type="button"
                    color="inherit"
                    size="small"
                    onClick={waiver.retryStatus}
                  >
                    Retry
                  </Button>
                }
              >
                We couldn&apos;t confirm your waiver, so we can&apos;t enter you
                yet. Please try again.
              </Alert>
            ) : !isFull &&
              !directPairingBlocked &&
              waiver.isBlocked &&
              !waiver.isLoading ? (
              <MaterialWaiverGate
                currentWaiver={waiver.currentWaiver}
                accepting={waiver.isAccepting}
                acceptError={Boolean(waiver.acceptError)}
                onAccept={() => {
                  if (waiverAcceptStartedRef.current) return;
                  waiverAcceptStartedRef.current = true;
                  waiver.accept(() => {
                    waiverAcceptStartedRef.current = false;
                  });
                }}
              />
            ) : null}

            {submitError ? (
              <Alert severity="error" role="alert">
                {submitError}
              </Alert>
            ) : null}

            {abandonError ? (
              <Alert severity="error" role="alert">
                {abandonError}
              </Alert>
            ) : null}

            <DialogActions sx={{ px: 0 }}>
              <Button
                autoFocus
                type="button"
                onClick={closeIfUnlocked}
                disabled={locked}
              >
                Cancel
              </Button>
              <Button
                type="button"
                variant="contained"
                onClick={handleSubmit}
                disabled={!canSubmit}
                aria-busy={submission.isPending || undefined}
              >
                {submission.isPending
                  ? recoveryPending && !scaRetryActive
                    ? "Finishing…"
                    : isSolo
                      ? "Entering…"
                      : "Registering…"
                  : recoveryPending && !scaRetryActive
                    ? "Finish registration"
                    : isSolo
                      ? "Enter solo"
                      : "Register"}
              </Button>
            </DialogActions>
          </Stack>
        )}
      </DialogContent>
    </Dialog>
  );
}

interface RegistrationComplianceFieldsProps {
  documents: TournamentLegalDocument[];
  acceptedDocumentKeys: Set<string>;
  onDocumentAcceptanceChange: (
    acceptanceKey: string,
    accepted: boolean,
  ) => void;
  tournamentLegalNotes: string | null;
  eventLegalNotes: string | null;
  tournamentTermsAccepted: boolean;
  eventTermsAccepted: boolean;
  onTournamentTermsAccepted: (accepted: boolean) => void;
  onEventTermsAccepted: (accepted: boolean) => void;
  requiresForeignDeclaration: boolean;
  foreignChoice: ForeignDeclarationChoice;
  onForeignChoiceChange: (choice: ForeignDeclarationChoice) => void;
  foreignCountries: ForeignCountryDraft[];
  onForeignCountryChange: (index: number, country: ForeignCountryDraft) => void;
  onAddForeignCountry: () => void;
  onRemoveForeignCountry: (index: number) => void;
  disabled: boolean;
}

function RegistrationComplianceFields({
  documents,
  acceptedDocumentKeys,
  onDocumentAcceptanceChange,
  tournamentLegalNotes,
  eventLegalNotes,
  tournamentTermsAccepted,
  eventTermsAccepted,
  onTournamentTermsAccepted,
  onEventTermsAccepted,
  requiresForeignDeclaration,
  foreignChoice,
  onForeignChoiceChange,
  foreignCountries,
  onForeignCountryChange,
  onAddForeignCountry,
  onRemoveForeignCountry,
  disabled,
}: RegistrationComplianceFieldsProps) {
  if (
    documents.length === 0 &&
    !tournamentLegalNotes &&
    !eventLegalNotes &&
    !requiresForeignDeclaration
  ) {
    return null;
  }

  return (
    <Stack spacing={2}>
      {documents.length > 0 ? (
        <FormControl component="fieldset" required>
          <FormLabel component="legend">Required documents</FormLabel>
          <Stack spacing={1.5} sx={{ mt: 1 }}>
            {documents.map((document) => (
              <Paper
                key={document.document_id}
                variant="outlined"
                sx={{ p: 2 }}
              >
                <Typography component="h3" variant="subtitle1">
                  {document.title}
                </Typography>
                <Box
                  sx={{ color: "text.secondary", typography: "body2", my: 1 }}
                >
                  {renderMarkdown(document.body)}
                </Box>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={acceptedDocumentKeys.has(
                        legalDocumentAcceptanceKey(document),
                      )}
                      onChange={(event) =>
                        onDocumentAcceptanceChange(
                          legalDocumentAcceptanceKey(document),
                          event.target.checked,
                        )
                      }
                      disabled={disabled}
                      required
                    />
                  }
                  label={`I accept ${document.title}`}
                />
              </Paper>
            ))}
          </Stack>
        </FormControl>
      ) : null}

      {tournamentLegalNotes ? (
        <LegacyTermsRequirement
          label="Tournament terms"
          acceptanceLabel="I accept the tournament terms"
          notes={tournamentLegalNotes}
          accepted={tournamentTermsAccepted}
          onAccepted={onTournamentTermsAccepted}
          disabled={disabled}
        />
      ) : null}

      {eventLegalNotes ? (
        <LegacyTermsRequirement
          label="Event terms"
          acceptanceLabel="I accept the event terms"
          notes={eventLegalNotes}
          accepted={eventTermsAccepted}
          onAccepted={onEventTermsAccepted}
          disabled={disabled}
        />
      ) : null}

      {requiresForeignDeclaration ? (
        <FormControl component="fieldset" required disabled={disabled}>
          <FormLabel component="legend">Foreign representation</FormLabel>
          <RadioGroup
            name="foreign-representation"
            value={foreignChoice}
            onChange={(event) =>
              onForeignChoiceChange(
                event.target.value as ForeignDeclarationChoice,
              )
            }
          >
            <FormControlLabel
              value="none"
              control={<Radio required />}
              label="I have not represented another country"
            />
            <FormControlLabel
              value="represented"
              control={<Radio required />}
              label="I have represented another country"
            />
          </RadioGroup>

          {foreignChoice === "represented" ? (
            <Stack spacing={1.5} sx={{ mt: 1 }}>
              {foreignCountries.map((country, index) => {
                const codeInvalid =
                  country.country.length > 0 &&
                  !/^[A-Z]{3}$/.test(country.country);
                const suffix =
                  foreignCountries.length > 1 ? ` ${index + 1}` : "";
                const codeId = `tr-foreign-country-${index}`;
                const codeHintId = `${codeId}-hint`;
                return (
                  <Paper key={index} variant="outlined" sx={{ p: 2 }}>
                    <Stack spacing={1.5}>
                      <TextField
                        id={codeId}
                        label={`Country code${suffix}`}
                        value={country.country}
                        onChange={(event) =>
                          onForeignCountryChange(index, {
                            ...country,
                            country: event.target.value
                              .replace(/[^a-z]/gi, "")
                              .slice(0, 3)
                              .toUpperCase(),
                          })
                        }
                        disabled={disabled}
                        required
                        error={codeInvalid}
                        helperText="Three-letter country code"
                        slotProps={{
                          htmlInput: {
                            autoComplete: "off",
                            maxLength: 3,
                            "aria-describedby": codeHintId,
                          },
                          formHelperText: { id: codeHintId },
                        }}
                      />
                      <TextField
                        id={`tr-foreign-period-${index}`}
                        label={`Representation period${suffix}`}
                        value={country.period}
                        onChange={(event) =>
                          onForeignCountryChange(index, {
                            ...country,
                            period: event.target.value,
                          })
                        }
                        disabled={disabled}
                      />
                      <TextField
                        id={`tr-foreign-event-${index}`}
                        label={`Last represented event${suffix}`}
                        value={country.lastEvent}
                        onChange={(event) =>
                          onForeignCountryChange(index, {
                            ...country,
                            lastEvent: event.target.value,
                          })
                        }
                        disabled={disabled}
                      />
                      {foreignCountries.length > 1 ? (
                        <Button
                          type="button"
                          size="small"
                          color="error"
                          onClick={() => onRemoveForeignCountry(index)}
                          disabled={disabled}
                          aria-label={`Remove country ${index + 1}`}
                        >
                          Remove
                        </Button>
                      ) : null}
                    </Stack>
                  </Paper>
                );
              })}
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={onAddForeignCountry}
                disabled={disabled}
              >
                Add another country
              </Button>
            </Stack>
          ) : null}
        </FormControl>
      ) : null}
    </Stack>
  );
}

function LegacyTermsRequirement({
  label,
  acceptanceLabel,
  notes,
  accepted,
  onAccepted,
  disabled,
}: {
  label: string;
  acceptanceLabel: string;
  notes: string;
  accepted: boolean;
  onAccepted: (accepted: boolean) => void;
  disabled: boolean;
}) {
  return (
    <FormControl component="fieldset" required disabled={disabled}>
      <FormLabel component="legend">{label}</FormLabel>
      <Box sx={{ color: "text.secondary", typography: "body2", my: 1 }}>
        {renderMarkdown(notes)}
      </Box>
      <FormControlLabel
        control={
          <Checkbox
            checked={accepted}
            onChange={(event) => onAccepted(event.target.checked)}
            disabled={disabled}
            required
          />
        }
        label={acceptanceLabel}
      />
    </FormControl>
  );
}

// ---------------------------------------------------------------------------
// Parental-consent pending confirmation (#1684) — shown in place of the form
// AFTER a MINOR's entry is created and the backend returns
// `pending_guardian_consent`. Honest copy: the place is held, the guardian was
// emailed, nothing else to do now. Its own Done control dismisses the dialog
// (the parent closes + the list refetches, where the row reads "Pending parental
// consent" via the shared status humanizer).
// ---------------------------------------------------------------------------

function GuardianConsentPending({
  onDone,
  doneRef,
  isPaid,
}: {
  onDone: () => void;
  doneRef: React.RefObject<HTMLButtonElement | null>;
  /** #1692 — a PAID entry defers the fee: the card is saved now, charged only
   *  when the guardian approves. Adds the charge-timing line; a FREE entry has no
   *  fee to defer, so it shows nothing money-related (no useless copy). */
  isPaid: boolean;
}) {
  return (
    <Stack spacing={1.5} role="status">
      <Typography variant="h6">Pending parental consent</Typography>
      <Typography variant="body2">
        We emailed your parent or guardian to approve this entry. Your place is
        held until they confirm — you don&apos;t need to do anything else right
        now.
      </Typography>
      {/* #1692 — deferred-charge honesty: the card is saved, not yet charged. */}
      {isPaid ? (
        <Typography variant="body2" color="text.secondary">
          Your card is saved now but not charged — the entry fee is only charged
          when your parent or guardian approves.
        </Typography>
      ) : null}
      <DialogActions sx={{ px: 0 }}>
        <Button
          ref={doneRef}
          type="button"
          variant="contained"
          onClick={onDone}
        >
          Done
        </Button>
      </DialogActions>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Card-save SCA challenge (#1692) — shown when the backend (402
// PAYMENT_AUTHENTICATION_REQUIRED) needs 3-D Secure before a paid minor's card can
// be saved off-session. Must render inside a <ConnectedElementsProvider> so
// `useStripe()` is scoped to the facility's connected account (the SetupIntent
// client_secret only confirms under that account).
//
// The player drives the challenge with an explicit click (a user gesture — the
// most reliable trigger for the 3-D Secure popup, and free of StrictMode
// effect-double-fire). On a confirmed SetupIntent it calls onAuthenticated() to
// RETRY the registration (card now saved → entry held pending consent); on a
// failed / cancelled challenge it surfaces a clear retry message. The BACKEND
// stays the gate — this only completes the Stripe-side authentication.
// ---------------------------------------------------------------------------

function RegisterPaymentScaChallenge({
  clientSecret,
  onAuthenticated,
  onProcessingChange,
}: {
  clientSecret: string;
  onAuthenticated: (authenticatedClientSecret: string) => Promise<void>;
  onProcessingChange: (processing: boolean) => void;
}) {
  const stripe = useStripe();
  const confirmIntent = useCallback(async (): Promise<boolean> => {
    if (!stripe) return false;
    const result = await stripe.confirmCardPayment(clientSecret);
    return !result.error && result.paymentIntent?.status === "succeeded";
  }, [clientSecret, stripe]);
  const { phase, busy, authenticate } = useStripeIntentAuthentication({
    clientSecret,
    confirmIntent,
    onAuthenticated,
    onProcessingChange,
  });
  const authenticationFailed = phase === "authentication_failed";
  const reconciliationFailed = phase === "reconciliation_failed";

  return (
    <Stack spacing={1.5} role="status">
      {authenticationFailed ? (
        <Alert severity="error" role="alert">
          We couldn&apos;t confirm this payment. Try again or choose another
          card.
        </Alert>
      ) : reconciliationFailed ? (
        <Alert severity="error" role="alert">
          Your payment was confirmed, but we couldn&apos;t finish the request.
          Try again.
        </Alert>
      ) : phase === "reconciling" ? (
        <Typography variant="body2">
          Your payment was confirmed. Finishing your request.
        </Typography>
      ) : phase === "complete" ? (
        <Typography variant="body2">Payment confirmed.</Typography>
      ) : (
        <Typography variant="body2">
          Confirm this payment with your bank to finish checkout.
        </Typography>
      )}
      <Box>
        <Button
          type="button"
          size="small"
          variant="contained"
          onClick={() => void authenticate()}
          disabled={!stripe || busy || phase === "complete"}
          aria-busy={busy || undefined}
        >
          {phase === "confirming"
            ? "Confirming…"
            : phase === "reconciling"
              ? "Finishing…"
              : authenticationFailed
                ? "Try again"
                : reconciliationFailed
                  ? "Finish request"
                  : phase === "complete"
                    ? "Payment confirmed"
                    : "Confirm payment"}
        </Button>
      </Box>
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Partner picker (doubles only) — reused verbatim from the old panel.
// ---------------------------------------------------------------------------

function PartnerPicker({
  query,
  onQueryChange,
  results,
  isSearching,
  hasQuery,
  selectedPartner,
  onSelect,
  onClear,
  disabled,
}: {
  query: string;
  onQueryChange: (value: string) => void;
  results: UserSearchResultResponse[];
  isSearching: boolean;
  hasQuery: boolean;
  selectedPartner: UserSearchResultResponse | null;
  onSelect: (partner: UserSearchResultResponse) => void;
  onClear: () => void;
  disabled: boolean;
}) {
  return (
    <Stack spacing={1.25}>
      <Typography variant="body2" sx={{ fontWeight: 700 }}>
        This is a doubles event — choose your partner to enter.
      </Typography>
      <Typography variant="body2" color="text.secondary">
        Your partner is added directly — there&apos;s no confirmation step, so
        they&apos;re entered right away.
      </Typography>

      {selectedPartner ? (
        <Paper variant="outlined" sx={{ px: 1.5 }}>
          <Typography variant="caption" color="text.secondary">
            Partner
          </Typography>
          <MaterialPlayerCard
            userId={selectedPartner.user_id}
            displayName={selectedPartner.display_name}
            actions={
              <Button
                type="button"
                variant="outlined"
                size="small"
                onClick={onClear}
                disabled={disabled}
              >
                Change
              </Button>
            }
          />
        </Paper>
      ) : (
        <>
          <Box
            component="form"
            role="search"
            onSubmit={(e) => e.preventDefault()}
          >
            <TextField
              fullWidth
              type="search"
              placeholder="Search by name"
              value={query}
              onChange={(e) => onQueryChange(e.target.value)}
              disabled={disabled}
              slotProps={{
                htmlInput: { "aria-label": "Search for a partner by name" },
              }}
            />
          </Box>

          {!hasQuery ? (
            <Typography variant="body2" color="text.secondary">
              Search by name to find your partner.
            </Typography>
          ) : isSearching ? (
            <Typography color="text.secondary" role="status">
              Searching…
            </Typography>
          ) : results.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No players found.
            </Typography>
          ) : (
            <List
              disablePadding
              component={Paper}
              variant="outlined"
              role="list"
            >
              {results.map((result) => {
                const identity = playerCardIdentityDisplay(
                  result.display_name,
                  result.user_id,
                );
                return (
                  <ListItem key={result.user_id} divider>
                    <MaterialPlayerCard
                      userId={result.user_id}
                      displayName={result.display_name}
                      actions={
                        <Button
                          type="button"
                          variant="outlined"
                          size="small"
                          onClick={() => onSelect(result)}
                          disabled={disabled}
                          aria-label={`Choose ${identity.name}`}
                        >
                          Choose
                        </Button>
                      }
                    />
                  </ListItem>
                );
              })}
            </List>
          )}
        </>
      )}
    </Stack>
  );
}

// ---------------------------------------------------------------------------
// Entry error → player copy, branched on HTTP STATUS. MODE-NEUTRAL: this dialog
// serves BOTH the per-event register flow and the doubles/mixed "Enter solo"
// flow (#117), so the copy reads correctly for either (an "entry", never only a
// "registration"). The per-status intent (402 decline / 409 conflict / 400
// eligibility) is unchanged — only the verb/noun is generalized.
// ---------------------------------------------------------------------------

const SERVER_WORDED_CONFLICTS = new Set([
  "EVENT_FULL",
  "MAX_EVENTS",
  "DUPLICATE_DISCIPLINE",
  "AGE_GROUP_SPAN",
  "REGISTRATION_WINDOW_CLOSED",
  "TOURNAMENT_NOT_OPEN",
  "OVERLAPPING_TOURNAMENT",
]);

function mapEntryError(error: unknown): string | null {
  if (!error) return null;
  if (!isApiError(error)) {
    return "We couldn't complete your entry. Please try again.";
  }
  const status = error.status;
  if (error.code === "REGISTRATION_PRICE_CHANGED") {
    return "Registration price changed. Refresh and try again.";
  }
  if (
    /player registration is required|must enter solo|doubles players/i.test(
      error.message,
    )
  ) {
    return "Each doubles player must enter separately, then pair from My entries.";
  }
  // #2691 entry rules: the server's own sentence ("This event is full", "You can
  // enter up to 3 events in this tournament.", "One level per discipline: …").
  if (status === 409 && SERVER_WORDED_CONFLICTS.has(error.code)) {
    return error.message;
  }
  if (status === 409) {
    return /already/i.test(error.message)
      ? "You're already registered."
      : "This event is full.";
  }
  if (status === 400) {
    // #1684 safety net — the backend fail-closes a minor's entry with
    // GuardianConsentRequiredError (400) when the guardian name/email is missing or
    // malformed. Map it to a clear, action-oriented inline message (the FE should
    // already be collecting these for a minor, so this is the belt-and-suspenders
    // path — e.g. the profile flag hadn't loaded yet).
    if (/guardian|parent/i.test(error.message)) {
      return "A parent or guardian's name and email are required to enter a player under 18.";
    }
    return error.message || "We couldn't enter you in this event.";
  }
  if (status === 402 || status === 422) {
    return "Your card was declined. Try a different card and try again.";
  }
  return "We couldn't complete your entry. Please try again.";
}
